import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUnifiedConversation } from '@/hooks/useUnifiedConversation';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Loader2, Send, User, Mail, HelpCircle, DollarSign,
  MessageSquare, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Sparkles,
  ThumbsUp, ThumbsDown, AlertTriangle, Paperclip, X
} from 'lucide-react';
import { CashPositionWidget } from '@/components/widgets/CashPositionWidget';
import { CashFlowChartWidget } from '@/components/widgets/CashFlowChartWidget';
import { PaymentAccelerationWidget } from '@/components/widgets/PaymentAccelerationWidget';
import { EmailListWidget } from '@/components/widgets/EmailListWidget';
import { HelpGuideWidget } from '@/components/widgets/HelpGuideWidget';
import { apiClient } from '@/lib/api-client';

interface UnifiedMessage {
  type: 'user' | 'assistant';
  content: string;
  primaryAgent: string;
  supportingAgents: string[];
  widget?: {
    type: string;
    data: any;
    actions: string[];
  };
  /**
   * Follow-up actions the router proposed for this answer (BUG-87 AC2). The
   * router has always emitted these; the panel simply never carried them, so
   * they were dropped on the floor between the response and the screen.
   */
  suggestedActions?: Array<{
    actionType: string;
    label: string;
    priority: number;
    agent?: string;
  }>;
  timestamp: Date;
  queryLogId?: string;
  feedbackGiven?: boolean;
}

/**
 * Render the assistant's `**bold**` markers as real emphasis (BUG-87 AC2).
 * The seeded cash-flow answer is written with `**…**` section headers; the
 * panel rendered the raw string, so the headline demo answer showed literal
 * asterisks. Split-on-delimiter rather than `dangerouslySetInnerHTML` — the
 * text carries live financial figures and must never be parsed as HTML.
 */
const renderEmphasis = (text: string): React.ReactNode =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );

const AgentIndicator = ({ agent }: { agent: string }) => {
  const getAgentIcon = (agentName: string) => {
    const name = agentName.toLowerCase();
    if (name.includes('tell tale') || name.includes('email')) return Mail;
    if (name.includes('zephyr') || name.includes('support')) return HelpCircle;
    if (name.includes('windrose') || name.includes('cash')) return DollarSign;
    return Sparkles;
  };

  const getAgentColor = (agentName: string) => {
    const name = agentName.toLowerCase();
    if (name.includes('tell tale')) return 'text-success';
    if (name.includes('zephyr')) return 'text-accent-foreground';
    if (name.includes('windrose')) return 'text-primary';
    if (name.includes('poniente')) return 'text-warning';
    return 'text-primary';
  };

  const Icon = getAgentIcon(agent);
  
  return (
    <div className={`flex items-center gap-1 ${getAgentColor(agent)}`}>
      <Icon className="h-3 w-3" />
      <span className="text-xs font-medium">{agent}</span>
    </div>
  );
};

const UnifiedWidgetRenderer = ({ widget, onAction }: {
  widget: any;
  onAction: (action: string, data?: any) => void;
}) => {
  // Hoisted out of the `default:` branch — calling a hook inside one switch arm
  // changes hook order between renders as the widget type changes, which is a
  // rules-of-hooks violation and a latent crash.
  const { t } = useTranslation();

  switch (widget.type) {
    case 'cash-position':
      return <CashPositionWidget data={widget.data} onAction={onAction} />;
    // BUG-87 AC2: the seeded cash-flow answer emits `cash-flow-chart`
    // (UnifiedChatRouter). With no case for it, it fell through to the generic
    // placeholder below and the demo's headline answer showed a grey box
    // reading "CASH FLOW-CHART" instead of the figures behind it.
    case 'cash-flow-chart':
      return <CashFlowChartWidget data={widget.data} onAction={onAction} />;
    case 'payment-acceleration':
      return <PaymentAccelerationWidget data={widget.data} onAction={onAction} />;
    case 'email-list':
      return <EmailListWidget data={widget.data} onAction={onAction} />;
    case 'help-guide':
      return <HelpGuideWidget data={widget.data} onAction={onAction} />;
    default:
      return (
        <div className="p-3 bg-muted rounded-lg text-sm">
          <p className="font-medium mb-1">{widget.type.replace('-', ' ').toUpperCase()}</p>
          <p className="text-muted-foreground text-xs">{t('chat.dataProcessed', 'Data processed by AI agents')}</p>
        </div>
      );
  }
};

export const AlwaysVisibleUnifiedChat = () => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [query, setQuery] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  // Conversation messages only — the greeting is rendered live (below) so it
  // always follows the CURRENT global language, never a mount-time snapshot (BUG-29).
  const [messages, setMessages] = useState<UnifiedMessage[]>([]);
  const greetingTimestamp = useRef(new Date());
  const greetingMessage: UnifiedMessage = {
    type: 'assistant',
    content: t('chat.welcomeMessage', '👋 Hi! I can help with emails, financial operations, payments, and support. What would you like to know?'),
    primaryAgent: 'AI Assistant',
    supportingAgents: [],
    timestamp: greetingTimestamp.current
  };

  const { processQuery, processQueryWithFile, isProcessing } = useUnifiedConversation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Send one query through the assistant.
   *
   * BUG-87 (regression): takes the query as an ARGUMENT rather than reading the
   * `query` state. The quick-query chips need to submit text they have only
   * just set, and `setQuery` is asynchronous — reading the state here would
   * send the previous value (or an empty string on the first click).
   */
  const submitQuery = async (currentQuery: string, currentFile: File | null) => {
    const userMessage: UnifiedMessage = {
      type: 'user',
      content: currentFile ? `${currentQuery} [Attached: ${currentFile.name}]` : currentQuery,
      primaryAgent: 'User',
      supportingAgents: [],
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      // Use processQueryWithFile if file is attached
      const response = currentFile
        ? await processQueryWithFile(currentQuery, currentFile)
        : await processQuery(currentQuery);

      const assistantMessage: UnifiedMessage = {
        type: 'assistant',
        content: response.conversationalText,
        primaryAgent: response.primaryAgent,
        supportingAgents: response.supportingAgents,
        widget: response.widget,
        suggestedActions: response.suggestedActions,
        timestamp: new Date(),
        queryLogId: response.queryLogId,
        feedbackGiven: false
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: UnifiedMessage = {
        type: 'assistant',
        content: t('chat.errorMessage', 'Sorry, I encountered an error. Please try again.'),
        primaryAgent: 'System',
        supportingAgents: [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isProcessing) return;

    const currentQuery = query;
    const currentFile = attachedFile;
    setQuery('');
    setAttachedFile(null);

    await submitQuery(currentQuery, currentFile);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setAttachedFile(file);
    } else if (file) {
      alert('Please select an Excel file (.xlsx or .xls)');
    }
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleWidgetAction = (action: string, data?: any) => {
    console.log('Widget action:', action, data);
  };

  const handleFeedback = async (
    messageIndex: number,
    feedbackType: 'positive' | 'negative',
    correctedAgent?: string
  ) => {
    const message = messages[messageIndex];
    if (!message.queryLogId || message.feedbackGiven) return;

    try {
      await apiClient.post('/ai/feedback', {
        queryLogId: message.queryLogId,
        feedbackType,
        correctedAgent,
        timestamp: new Date().toISOString()
      });

      // Mark feedback as given
      setMessages(prev => prev.map((msg, idx) =>
        idx === messageIndex
          ? { ...msg, feedbackGiven: true }
          : msg
      ));

    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleCorrection = async (messageIndex: number) => {
    const message = messages[messageIndex];
    if (!message.queryLogId) return;

    // Agent options
    const agents = [
      'Windrose', 'Poniente', 'Levante',
      'Hooked In', 'Tell Tale', 'Full Send', 'Zephyr'
    ];

    // Simple prompt (can be replaced with modal later)
    const correctedAgent = prompt(
      `Current agent: ${message.primaryAgent}\n\nWhich agent should have handled this query?\n\n${agents.join('\n')}`
    );

    if (correctedAgent && agents.includes(correctedAgent)) {
      await handleFeedback(messageIndex, 'negative', correctedAgent);
    }
  };

  /**
   * BUG-87 (regression, second symptom): the chips now SEND.
   *
   * They used to only drop their text into the input and stop there, so
   * clicking "¿Cuál es mi posición de efectivo?" produced no observable
   * response — the user had to notice the box had filled and press send. A
   * chip that reads as a button and answers nothing reads as a broken app.
   */
  const handleQuickQuery = (quickQuery: string) => {
    // Auto-expand when clicking quick query
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    if (isProcessing) return;

    const currentFile = attachedFile;
    setQuery('');
    setAttachedFile(null);
    void submitQuery(quickQuery, currentFile);
  };

  const handleInputFocus = () => {
    // Auto-expand chat when user focuses on input
    if (isCollapsed) {
      setIsCollapsed(false);
    }
  };

  const quickQueries = [
    t('chat.quickQuery1', "What's my cash position?"),
    t('chat.quickQuery2', "Process my emails"),
    t('chat.quickQuery3', "Help me with payments"),
    t('chat.quickQuery4', "Show overdue invoices")
  ];

  return (
    <div className={`w-full transition-all duration-300 ${
      isCollapsed ? 'h-12' : 'h-96'
    } border-t bg-background relative`}>
      {/* Collapsed State - Minimal Input Bar */}
      {isCollapsed && (
        <div className="h-full flex items-center px-4 gap-2 cursor-pointer" onClick={() => setIsCollapsed(false)}>
          <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-sm text-muted-foreground flex-1">{t('chat.placeholder', 'Ask anything...')}</span>
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {!isCollapsed && (
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-3 bg-muted border-b">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-semibold text-foreground">{t('chat.aiAssistant', 'AI Assistant')}</span>
              <Badge variant="secondary" className="bg-accent text-accent-foreground ml-auto">
                {t('chat.smart', 'Smart')}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-1">{t('chat.askAnything', 'Ask anything about your business')}</p>
          </div>

          {/* Messages Area */}
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-3">
              {[greetingMessage, ...messages].map((msg, idx) => (
                <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[90%] ${msg.type === 'user' ? 'order-2' : 'order-1'}`}>
                    {/* Agent Info */}
                    {msg.type === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2">
                        <AgentIndicator agent={msg.primaryAgent} />
                        {msg.supportingAgents.length > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">+</span>
                            {msg.supportingAgents.slice(0, 1).map((agent, i) => (
                              <AgentIndicator key={i} agent={agent} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Message Content */}
                    <div className={`rounded-lg p-4 text-sm leading-normal ${
                      msg.type === 'user'
                        ? 'bg-accent0 text-white'
                        : 'bg-muted text-foreground'
                    }`}>
                      <div className="whitespace-normal break-words">{renderEmphasis(msg.content)}</div>
                    </div>

                    {/* Widget Rendering */}
                    {msg.widget && (
                      <div className="mt-2 bg-background rounded-lg border shadow-sm overflow-hidden">
                        <UnifiedWidgetRenderer
                          widget={msg.widget}
                          onAction={handleWidgetAction}
                        />
                      </div>
                    )}

                    {/* Follow-up actions (BUG-87 AC2). The router proposes these
                        per answer; before this they were dropped by the panel. */}
                    {msg.type === 'assistant' && msg.suggestedActions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {[...msg.suggestedActions]
                          .sort((a, b) => a.priority - b.priority)
                          .slice(0, 3)
                          .map((action) => (
                            <Button
                              key={action.actionType}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleWidgetAction(action.actionType, action)}
                            >
                              {action.label}
                            </Button>
                          ))}
                      </div>
                    )}

                    {/* Feedback Buttons */}
                    {msg.type === 'assistant' && msg.queryLogId && !msg.feedbackGiven && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFeedback(idx, 'positive')}
                          className="text-success hover:text-success hover:bg-success-muted h-7 px-2 text-xs"
                        >
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          Helpful
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFeedback(idx, 'negative')}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2 text-xs"
                        >
                          <ThumbsDown className="h-3 w-3 mr-1" />
                          Not Helpful
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCorrection(idx)}
                          className="text-warning hover:text-warning hover:bg-warning-muted h-7 px-2 text-xs"
                        >
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Wrong Agent
                        </Button>
                      </div>
                    )}

                    {/* Feedback Confirmation */}
                    {msg.feedbackGiven && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        ✓ Feedback recorded
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">{t('chat.thinking', 'Thinking...')}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Quick Queries */}
          <div className="px-4 py-2 border-t">
            <div className="grid grid-cols-2 gap-2">
              {quickQueries.map((quickQuery, idx) => (
                <Button
                  key={idx}
                  variant="ghost"
                  size="sm"
                  className="text-xs h-8 text-primary hover:bg-accent px-2 py-1 justify-start overflow-hidden"
                  onClick={() => handleQuickQuery(quickQuery)}
                  disabled={isProcessing}
                >
                  <span className="truncate w-full text-left">{quickQuery}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t bg-muted">
            <form onSubmit={handleSubmit} className="space-y-2">
              {/* Attached File Indicator */}
              {attachedFile && (
                <div className="flex items-center gap-2 px-3 py-2 bg-accent rounded-lg text-sm">
                  <Paperclip className="h-3 w-3 text-primary" />
                  <span className="flex-1 text-primary truncate">{attachedFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    className="h-5 w-5 p-0 text-primary hover:text-primary"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              <div className="flex gap-2">
                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* File Attach Button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing}
                  className="px-3"
                  title="Attach Excel file"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>

                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder={t('chat.placeholder', 'Ask anything...')}
                  disabled={isProcessing}
                  className="flex-1 text-sm"
                />
                <Button
                  type="submit"
                  disabled={isProcessing || !query.trim()}
                  size="sm"
                  className="px-3"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};